class StickyNav extends HTMLElement {
  constructor() {
    super();
    this.links = this.querySelectorAll('[data-scroll-link]');
    this.progressBar = this.querySelector('[data-scroll-progress]');
    this.disclosure = this.querySelector('.sticky-nav__disclosure');
    this.summary = this.querySelector('.sticky-nav__summary');
    this.ticking = false;
  }

  connectedCallback() {
    this.links.forEach((link) => {
      link.addEventListener('click', this.onLinkClick);
    });
    this.summary?.addEventListener('click', this.onSummaryClick);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.updateProgress);
    this.updateProgress();
    this.updateScrolledState();

    // <site-header> sits earlier in the DOM and sets --header-offset /
    // --announcement-bar-height in its own connectedCallback, so by the
    // time we're upgraded here those vars are already correct — reveal now
    // instead of showing at the CSS fallback position first (see
    // site-header.js for the full explanation of the jump this avoids).
    this.classList.add('sticky-nav--ready');
  }

  disconnectedCallback() {
    this.links.forEach((link) => {
      link.removeEventListener('click', this.onLinkClick);
    });
    this.summary?.removeEventListener('click', this.onSummaryClick);

    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.updateProgress);
  }

  // The <details> stays permanently `open` (see sticky-nav.liquid) so the
  // browser's native closed-state hiding — content-visibility: hidden in
  // current Chrome — never kicks in and collapses the list's height. The
  // tablet/mobile collapsed look is instead driven entirely by this class.
  onSummaryClick = (event) => {
    event.preventDefault();
    this.disclosure?.classList.toggle('is-expanded');
  };

  onLinkClick = (event) => {
    const href = event.currentTarget.getAttribute('href');
    if (!href || href.charAt(0) !== '#' || href.length < 2) return;

    let target;
    try {
      target = document.querySelector(href);
    } catch (error) {
      return;
    }
    if (!target) return;

    event.preventDefault();

    // Collapse the tablet/mobile dropdown before measuring — otherwise the
    // offset below is based on the expanded height of the nav, not the
    // collapsed bar it'll actually be once the page has scrolled.
    this.disclosure?.classList.remove('is-expanded');

    const rootStyle = getComputedStyle(document.documentElement);
    const announcementBarHeight = parseFloat(rootStyle.getPropertyValue('--announcement-bar-height')) || 0;
    const headerOffset = parseFloat(rootStyle.getPropertyValue('--header-offset')) || 0;
    const offset = this.getBoundingClientRect().height + announcementBarHeight + headerOffset;
    const targetTop = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top: targetTop, behavior: 'smooth' });
  };

  onScroll = () => {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.updateProgress();
      this.updateScrolledState();
      this.ticking = false;
    });
  };

  updateScrolledState = () => {
    this.classList.toggle('sticky-nav--scrolled', window.scrollY > 0);
  };

  updateProgress = () => {
    if (!this.progressBar) return;

    const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = scrollableHeight > 0 ? window.scrollY / scrollableHeight : 0;
    this.progressBar.style.transform = `scaleX(${Math.min(Math.max(progress, 0), 1)})`;
  };
}

customElements.define('sticky-nav', StickyNav);
